// ===============================================================
// ProBank — Make a Transaction (wallet-branded selector)
//
// Opens when a user taps a send button inside a wallet.
// Reads the wallet it came from (state.from) and skins itself
// to match that wallet's identity:
//   opay       → Opay green
//   palmpay    → PalmPay purple/pink
//   kuda       → Kuda violet
//   moniepoint → Moniepoint deep blue/teal
//
// Options shown:
//   📟 POS Terminal
//   🏦 Bank Account
//   📱 Loan App Payment
//   🌍 International Transfer (Elite members only)
// ===============================================================

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCountry } from "../context/CountryContext";

const TransferSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGhana, config } = useCountry();

  // Which wallet sent us here? Default: opay-style (the main wallet).
  const from = location.state?.from || "opay";

  // Read the logged-in user to determine plan access.
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    user = {};
  }

  const plan = (user.plan || "").toLowerCase();
  const isEliteActive = plan === "elite" && user.subscriptionActive === true;

  // 🎨 Per-wallet brand skins — each wallet keeps its own identity.
  const BRAND = {
    opay: {
      name: isGhana ? "MoMo Wallet" : "ProBank Wallet",
      bg: isGhana ? "bg-[#0a0500]" : "bg-black",
      card: isGhana ? "bg-[#1a0a00] border-yellow-500/20" : "bg-gray-900 border-white/5",
      iconBg: isGhana ? "bg-yellow-500/10" : "bg-emerald-500/10",
      accent: isGhana ? "text-yellow-400" : "text-emerald-400",
      hover: isGhana ? "hover:border-yellow-500/50" : "hover:border-emerald-500/50",
      emoji: "💚",
    },
    palmpay: {
      name: isGhana ? "Cedi Wallet" : "PalmPay",
      bg: isGhana ? "bg-[#0d0000]" : "bg-black",
      card: isGhana ? "bg-[#1a0505] border-red-500/15" : "bg-[#15151a] border-purple-500/15",
      iconBg: isGhana ? "bg-red-500/10" : "bg-purple-500/10",
      accent: isGhana ? "text-red-300" : "text-purple-300",
      hover: isGhana ? "hover:border-red-500/50" : "hover:border-purple-500/50",
      emoji: "💜",
    },
    kuda: {
      name: "Kuda",
      bg: "bg-[#0b0713]",
      card: "bg-[#170f26] border-violet-500/15",
      iconBg: "bg-violet-500/10",
      accent: "text-violet-300",
      hover: "hover:border-violet-500/50",
      emoji: "💜",
    },
    moniepoint: {
      name: isGhana ? "GCB Bank" : "Moniepoint",
      bg: "bg-[#040d16]",
      card: "bg-[#0a1a2a] border-sky-500/15",
      iconBg: "bg-sky-500/10",
      accent: "text-sky-300",
      hover: "hover:border-sky-500/50",
      emoji: "💙",
    },
  };

  const brand = BRAND[from] || BRAND.opay;

  const options = [
    {
      key: "pos",
      icon: "📟",
      title: "Pay a POS Terminal",
      description: "Send money to a POS agent account with instant confirmation",
      path: "/transfer/pos",
    },
    {
      key: "bank",
      icon: "🏦",
      title: "Send to Bank Account",
      description: "Transfer to any supported bank or mobile money account",
      path: "/transfer/bank",
    },
    {
      key: "loan",
      icon: "📱",
      title: "Pay a Loan App",
      description: "Settle a loan directly to a loan application account",
      path: "/transfer/loan",
    },
  ];

  // International transfers are an Elite-only feature. The option
  // is completely hidden from non-Elite users.
  if (isEliteActive) {
    options.push({
      key: "international",
      icon: "🌍",
      title: "International Transfer",
      description: "Send money to any international country",
      path: "/transfer/international",
    });
  }

  return (
    <div className={`min-h-screen ${brand.bg} text-white px-4 py-8`}>
      <div className="max-w-md mx-auto">
        {/* Header row — wallet brand + country badge */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-300 text-xl leading-none px-1"
              aria-label="Back"
            >
              ←
            </button>
            <span className="text-sm text-gray-400">
              {brand.emoji} {brand.name}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50">
            <span className="text-base leading-none">{isGhana ? "🇬🇭" : "🇳🇬"}</span>
            <span className="text-xs text-slate-300 font-medium">{config.name}</span>
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-1">Make a Transaction</h1>
        <p className="text-sm text-gray-400 mb-8">
          Where are you sending to?
        </p>

        <div className="space-y-4">
          {options.map((opt) => (
            <button
              key={opt.key}
              onClick={() => navigate(opt.path, { state: { from } })}
              className={`w-full flex items-center gap-4 border rounded-2xl p-5 text-left transition-colors ${brand.card} ${brand.hover}`}
            >
              <div className={`w-12 h-12 rounded-xl ${brand.iconBg} flex items-center justify-center text-2xl flex-shrink-0`}>
                {opt.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white">{opt.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{opt.description}</p>
              </div>
              <span className={`${brand.accent} text-lg`}>›</span>
            </button>
          ))}
        </div>

        <p className="text-center text-xs text-gray-600 mt-10">
          All transactions are deducted from your wallet balance and recorded in your history.
        </p>
      </div>
    </div>
  );
};

export default TransferSelect;
